import { Trash, ChevronsUpDown, Copy, Check } from "lucide-react";
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setLastEditedDocumentField } from '../../../store/reducers/index';
import { DocumentosModal, getDocumentos } from './DocumentosModal';
import { DocumentosTipoModal, getDocumentosTipos } from './DocumentosModal';

// Reusable Input with Copy Button Component
const InputWithCopy = ({ id, value, placeholder, onChange, className, type = "text", required = false, maxLength }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (value === null || value === undefined || value === '') return;
    
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Check if value exists (including 0 as a valid value)
  const hasValue = value !== null && value !== undefined && value !== '';

  // Handle value properly - don't convert 0 to empty string
  const inputValue = value === null || value === undefined ? "" : value;

  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        value={inputValue}
        placeholder={placeholder}
        onChange={onChange}
        className={className}
        required={required}
        maxLength={maxLength}
      />
      {hasValue && (
        <button
          type="button"
          onClick={handleCopy}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
          title={copied ? "Copied!" : "Copy"}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-gray-500" />
          )}
        </button>
      )}
    </div>
  );
};

export const DocumentsTableContent = ({
  declarationIndex,
  itemIndex,
  isSpanish,
  item,
  editableData,
  setEditableData,
  setIsDirty,
  openDocModalIndex,
  setOpenDocModalIndex,
  openTipoModal,
  setOpenTipoModal,
}) => {
  const dispatch = useDispatch();
  const documentos = getDocumentos();

  const handleDocumentChange = (
    declarationIndex,
    itemIndex,
    docIndex,
    field,
    value
  ) => {
    // Validate inputs
    if (declarationIndex === undefined || itemIndex === undefined || docIndex === undefined || field === undefined) {
      console.warn('handleDocumentChange: Missing required parameters');
      return;
    }
    
    // Validate editableData
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      console.warn('handleDocumentChange: editableData is invalid or empty');
      return;
    }
    
    // Validate declarationIndex bounds
    if (declarationIndex < 0 || declarationIndex >= editableData.length) {
      console.warn(`handleDocumentChange: declarationIndex ${declarationIndex} out of bounds`);
      return;
    }
    
    const declaration = editableData[declarationIndex];
    if (!declaration || typeof declaration !== 'object') {
      console.warn(`handleDocumentChange: Invalid declaration at index ${declarationIndex}`);
      return;
    }
    
    // Validate partidas array
    if (!declaration.partidas || !Array.isArray(declaration.partidas)) {
      console.warn(`handleDocumentChange: No partidas array in declaration ${declarationIndex}`);
      return;
    }
    
    // Validate itemIndex bounds
    if (itemIndex < 0 || itemIndex >= declaration.partidas.length) {
      console.warn(`handleDocumentChange: itemIndex ${itemIndex} out of bounds`);
      return;
    }
    
    const item = declaration.partidas[itemIndex];
    if (!item || typeof item !== 'object') {
      console.warn(`handleDocumentChange: Invalid item at index ${itemIndex}`);
      return;
    }
    
    // Validate documentos array
    if (!item.documentos || !Array.isArray(item.documentos)) {
      console.warn(`handleDocumentChange: No documentos array in item ${itemIndex}`);
      return;
    }
    
    // Validate docIndex bounds
    if (docIndex < 0 || docIndex >= item.documentos.length) {
      console.warn(`handleDocumentChange: docIndex ${docIndex} out of bounds`);
      return;
    }
    
    const documento = item.documentos[docIndex];
    if (!documento || typeof documento !== 'object') {
      console.warn(`handleDocumentChange: Invalid documento at index ${docIndex}`);
      return;
    }
    
    try {
    // Deep clone to avoid mutating original data
    const updatedData = JSON.parse(JSON.stringify(editableData));
      updatedData[declarationIndex].partidas[itemIndex].documentos[docIndex][field] = value;
      setEditableData(updatedData);
      // Don't update extractedData here - only update on save
      setIsDirty(true);
      
      dispatch(setLastEditedDocumentField({
        field,
        value,
        declarationIndex,
        partidaIndex: itemIndex,
        documentIndex: docIndex,
      }));
    } catch (error) {
      console.error('handleDocumentChange: Error updating document:', error);
    }
  };

  return (
    <div className="overflow-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="py-2 px-4 pt-2 pl-0">
              <p className="w-max">#</p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">
                {!isSpanish ? "Document Code" : "Código de Documento"}
              </p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">{!isSpanish ? "Type" : "Tipo"}</p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">{!isSpanish ? "Number" : "Número"}</p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">{!isSpanish ? "Date" : "Fecha"}</p>
            </th>
            <th className="py-2 px-4 pt-2"></th>
          </tr>
        </thead>
        <tbody>
          {item.documentos?.map((doc, docIndex) => (
            <tr key={docIndex}>
              <td className="py-4 px-4 pl-0 border-t">
                <span className="text-sm">{docIndex + 1}</span>
              </td>
              <td className="py-4 px-4 border-t">
                <div className="relative w-full">
                  <button
                    type="button"
                    className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${doc.codigo_documento ? '' : 'opacity-75'}`}
                    style={{ maxWidth: '260px' }}
                    onClick={() => setOpenDocModalIndex(`${declarationIndex}_${itemIndex}_${docIndex}`)}
                    id={`documents_codigo_documento_${declarationIndex + 1}_${itemIndex}_${docIndex}`}
                  >
                    {doc.codigo_documento ? (
                      (() => {
                        const selectedDoc = documentos.find(d => d.codigo === doc.codigo_documento || d.nombre === doc.codigo_documento);
                        return selectedDoc ? (
                          <span
                            className="truncate"
                            style={{ maxWidth: '200px', display: 'inline-block' }}
                            title={`${selectedDoc.codigo} - ${selectedDoc.nombre}`}
                          >
                            {selectedDoc.codigo} {selectedDoc.nombre && <span className="text-xs text-gray-400 ml-2">({selectedDoc.nombre})</span>}
                          </span>
                        ) : (
                          <span className="truncate" title={doc.codigo_documento}>{doc.codigo_documento}</span>
                        );
                      })()
                    ) : (
                      <span className="truncate" title={!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}>{!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}</span>
                    )}
                    <ChevronsUpDown className="w-4 h-4" />
                  </button>
                  <DocumentosModal
                    documentos={documentos}
                    isOpen={openDocModalIndex === `${declarationIndex}_${itemIndex}_${docIndex}`}
                    onClose={() => setOpenDocModalIndex(null)}
                    onSelect={(docSelected) => {
                      handleDocumentChange(
                        declarationIndex,
                        itemIndex,
                        docIndex,
                        'codigo_documento',
                        docSelected.codigo
                      );
                    }}
                    selectedDocumento={documentos.find(d => d.codigo === doc.codigo_documento || d.nombre === doc.codigo_documento)}
                    title={!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}
                  />
                </div>
              </td>
              <td className="py-4 px-4 border-t">
                <button
                  type="button"
                  className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${doc.tipo ? '' : 'opacity-75'}`}
                  onClick={() => setOpenTipoModal({ [`${declarationIndex}_${itemIndex}_${docIndex}`]: true })}
                  id={`documents_tipo_${declarationIndex + 1}_${itemIndex}_${docIndex}`}
                >
                  {(() => {
                    const tipos = getDocumentosTipos();
                    const selectedTipo = tipos.find(t => t.codigo === String(doc.tipo));
                    if (selectedTipo) {
                      return <span className="truncate">{selectedTipo.codigo} <span className="text-xs text-gray-400 ml-2">({selectedTipo.nombre})</span></span>;
                    } else if (doc.tipo) {
                      return <span className="truncate" title={doc.tipo}>{doc.tipo}</span>;
                    } else {
                      return <span className="truncate">{!isSpanish ? 'Select Type' : 'Seleccionar Tipo'}</span>;
                    }
                  })()}
                  <ChevronsUpDown className="w-4 h-4" />
                </button>
                <DocumentosTipoModal
                  tipos={getDocumentosTipos()}
                  isOpen={!!openTipoModal[`${declarationIndex}_${itemIndex}_${docIndex}`]}
                  onClose={() => setOpenTipoModal({ ...openTipoModal, [`${declarationIndex}_${itemIndex}_${docIndex}`]: false })}
                  onSelect={tipo => handleDocumentChange(
                    declarationIndex,
                    itemIndex,
                    docIndex,
                    'tipo',
                    Number(tipo.codigo)
                  )}
                  selectedTipo={getDocumentosTipos().find(t => t.codigo === String(doc.tipo))}
                  title={!isSpanish ? 'Select Type' : 'Seleccionar Tipo'}
                />
              </td>
              <td className="py-4 px-4 border-t">
                <InputWithCopy
                  id={`documents_numero_${declarationIndex + 1}_${itemIndex}_${docIndex}`}
                  value={doc.numero}
                  placeholder={`${
                    !doc.numero && !isSpanish
                      ? "Enter Number"
                      : "Entrada Número"
                  }`}
                  onChange={(e) =>
                    handleDocumentChange(
                      declarationIndex,
                      itemIndex,
                      docIndex,
                      "numero",
                      e.target.value
                    )
                  }
                  className="w-full border rounded px-2 py-1 pr-8"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <InputWithCopy
                  id={`documents_fecha_${declarationIndex + 1}_${itemIndex}_${docIndex}`}
                  value={doc.fecha}
                  placeholder={`${
                    !doc.fecha && !isSpanish ? "Enter Date" : "Entrada Fecha"
                  }`}
                  onChange={(e) =>
                    handleDocumentChange(
                      declarationIndex,
                      itemIndex,
                      docIndex,
                      "fecha",
                      e.target.value
                    )
                  }
                  className="w-full border rounded px-2 py-1 pr-8"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <button
                  className="py-1 text-xs flex items-center gap-1 text-red-500"
                  onClick={() => {
                    // Deep clone to avoid mutating original data
                    const updatedData = JSON.parse(JSON.stringify(editableData));
                    updatedData[declarationIndex].partidas[
                      itemIndex
                    ].documentos.splice(docIndex, 1);
                    setEditableData(updatedData);
                    // Don't update extractedData here - only update on save
                    setIsDirty(true);
                dispatch(setLastEditedDocumentField({
                  field: '__documents__',
                  value: null,
                  declarationIndex,
                  partidaIndex: itemIndex,
                  documentIndex: docIndex,
                }));
                  }}
                >
                  <Trash className={`size-4 stroke-red-500`} />
                  {isSpanish ? "Eliminar" : "Delete"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

